// src/pages/CasesPage.js — Cinematic Cases grid + modal detail
// Drop-in replacement. No external libs beyond what's already in package.json.
// Mobile-ready. Uses framer-motion shared layout for seamless open/close.

import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { AnimatePresence, motion, useMotionValue, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { trackEvent } from "../lib/analytics";

/*
  Notes
  - Safe with existing animations. We isolate z-index and set [data-contrast-shelter]
  - Modal toggles the same `data-overlay-open` attribute used by MenuOverlay
  - Lenis scroll paused while modal is open
  - Works without real images; gradient covers fallback if /gemeente_ai_cover.jpg missing
*/

const Z = 60; // above MenuOverlay (50)


// smooth inertial scroll inside modal, independent of Lenis
function useInertialScroll(ref) {
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let vel = 0;
    let frame = null;
    let lastTouchY = null;
    let dragging = false;

    // tuning values
    const TOUCH_MULTIPLIER = 0.45;   // lower = slower finger drag
    const WHEEL_MULTIPLIER = 1;      // keep wheel normal
    const MAX_VEL = 40;              // px per frame cap
    const FRICTION_IDLE = 0.90;      // after release. higher = longer glide
    const FRICTION_DRAG = 0.6;       // while finger down. higher = more "locked" / direct

    const step = () => {
      // move
      el.scrollTop += vel;

      // pick friction
      const friction = dragging ? FRICTION_DRAG : FRICTION_IDLE;
      vel *= friction;

      // stop loop when basically zero
      if (Math.abs(vel) < 0.1) {
        frame = null;
        return;
      }
      frame = requestAnimationFrame(step);
    };

    const kick = (delta) => {
      vel += delta;

      // clamp so a big swipe does not yeet
      if (vel > MAX_VEL) vel = MAX_VEL;
      if (vel < -MAX_VEL) vel = -MAX_VEL;

      if (!frame) frame = requestAnimationFrame(step);
    };

    const onWheel = (e) => {
      e.stopPropagation();
      e.preventDefault();
      kick(e.deltaY * WHEEL_MULTIPLIER);
    };

    const onTouchStart = (e) => {
      dragging = true;
      lastTouchY = e.touches[0].clientY;
      // when you grab again mid-glide you "take control"
      // so kill leftover inertial velocity
      vel = 0;
    };

    const onTouchMove = (e) => {
      const y = e.touches[0].clientY;
      const dy = lastTouchY - y; // same sign as scrollTop delta
      lastTouchY = y;

      e.stopPropagation();
      e.preventDefault();

      // feed smaller delta for more natural drag speed
      kick(dy * TOUCH_MULTIPLIER);
    };

    const onTouchEnd = () => {
      dragging = false;
      // after finger release we keep vel and switch to idle friction
      if (!frame) frame = requestAnimationFrame(step);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    el.addEventListener("touchcancel", onTouchEnd, { passive: false });

    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref]);
}


function LockIcon(props) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}


export default function CasesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [openId, setOpenId] = useState(null);
  const [hideSource, setHideSource] = useState(false);
  const prefersReduced = usePrefersReducedMotion();

  // Data — add more cases later
  const cases = useMemo(() => {
    const entries = t("cases.entries", { returnObjects: true });
    if (!Array.isArray(entries)) return [];
    return entries.map((entry) => ({
      ...entry,
      isExternal: Boolean(entry.externalUrl),
      externalLabel: entry.externalLabel || t("cases.external"),
      badges: Array.isArray(entry.badges) ? entry.badges : [],
      outcomes: Array.isArray(entry.outcomes) ? entry.outcomes : [],
      stack: Array.isArray(entry.stack) ? entry.stack : [],
      sections: Array.isArray(entry.sections)
        ? entry.sections.map((section) => ({
            ...section,
            media: Array.isArray(section.media) ? section.media : [],
          }))
        : [],
    }));
  }, [t]);

  const directCase = caseId ? cases.find((entry) => entry.id === caseId) : null;

  const openCase = (entry) => {
    trackEvent("case_card_click", {
      source: "cases-page",
      caseId: entry.id,
      external: Boolean(entry.externalUrl),
    });

    if (entry.externalUrl) {
      window.open(entry.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(`/work/${entry.id}`);
  };

  useEffect(() => {
    const stop = () => window.lenis?.stop?.();
    const start = () => window.lenis?.start?.();
    if (openId) {
      document.body.style.overflow = "hidden";
      stop();
      document.documentElement.setAttribute("data-overlay-open", "");
      const onKey = (e) => e.key === "Escape" && setOpenId(null);
      window.addEventListener("keydown", onKey);
      return () => {
        window.removeEventListener("keydown", onKey);
      };
    } else {
      document.body.style.overflow = "";
      start();
      document.documentElement.removeAttribute("data-overlay-open");
    }
  }, [openId]);

  if (caseId) {
    if (!directCase || directCase.locked) {
      return (
        <Page $shelter>
          <CaseNotFound>
            <Kicker>{t("cases.title")}</Kicker>
            <Title>{t("cases.detail.notFoundTitle")}</Title>
            <Lead>{t("cases.detail.notFoundText")}</Lead>
            <CasePrimaryCta as={Link} to="/work">
              {t("cases.detail.backToWork")}
            </CasePrimaryCta>
          </CaseNotFound>
        </Page>
      );
    }

    return <CaseStoryPage data={directCase} />;
  }

  return (
    <Page $shelter>
      <Header>
        <Kicker>{t("sidebar.cases") || "CASES"}</Kicker>
        <Title>{t("cases.title")}</Title>
        <Lead>
          {t("cases.clickCard")}
        </Lead>
      </Header>

      <Ambient />

      <Grid>
        {cases.map((c, idx) => (
          <CaseCard
            key={c.id}
            data={c}
            index={idx}
            onOpen={() => openCase(c)}
            isGhostHidden={hideSource && openId === c.id}
          />
        ))}
      </Grid>

      <AnimatePresence>
        {openId && (
          <Modal onClose={() => {
            setOpenId(null);
            setHideSource(false);

          }}>
            <CaseDetail 
            data={cases.find((c) => c.id === openId)} 
            prefersReduced={prefersReduced}
            onReady={() => setHideSource(true)}
            />
          </Modal>
        )}
      </AnimatePresence>
    </Page>
  );
}

/* ========================= Components ========================= */

function CaseCard({ data, index, onOpen, isGhostHidden }) {
  const cardRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useTransform(my, [0, 1], [8, -8]);
  const rotateY = useTransform(mx, [0, 1], [-10, 10]);

  const onMove = (e) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    mx.set(x);
    my.set(y);
  };

  const onLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <CardWrap
      ref={cardRef}
      style={{ rotateX, rotateY }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
      data-locked={data.locked ? "" : undefined}
      onClick={() => !data.locked && onOpen()}
      role={data.locked ? "group" : "button"}
      aria-label={data.title}
      tabIndex={data.locked ? -1 : 0}
      onKeyDown={(e) => !data.locked && (e.key === "Enter" || e.key === " ") && onOpen()}
      layoutId={`card-${data.id}`}
      data-hidden={isGhostHidden ? "" : undefined}
    >
      <Shine
        style={{
          background:
            "radial-gradient(400px 200px at calc(" +
            useMotion(mx, (v) => `${v * 100}%`) +
            ") calc(" +
            useMotion(my, (v) => `${v * 100}%`) +
            "), rgba(255,255,255,.12), transparent 60%)",
        }}
      />

      <CardBorder />

      <Cover
        layoutId={`cover-${data.id}`}
        $hasImg={!!data.cover}
        $locked={data.locked}
      >
        {data.cover && (
          <CoverImg
            src={data.cover}
            alt=""
            $fit={data.coverFit || "contain"}
            $position={data.coverPosition || "center"}
            $scale={data.coverScale || 1}
          />
        )}

        {data.locked && (
          <LockedOverlay>
            <div>
              <LockedBadge>
                <LockIcon />
                Locked
              </LockedBadge>
              <LockedText>Coming soon</LockedText>
            </div>
          </LockedOverlay>
        )}
      </Cover>

      <CardBody>
        <Row>
          <TitleRow>
            <CardTitle>{data.title}</CardTitle>
            {data.year ? <Year>{data.year}</Year> : null}
          </TitleRow>
          {data.isExternal || data.badges.length > 0 ? (
            <Badges>
              {data.isExternal ? <Badge>{data.externalLabel}</Badge> : null}
              {data.badges.map((b) => (
                <Badge key={b}>{b}</Badge>
              ))}
            </Badges>
          ) : null}
        </Row>
        <Sub>{data.subtitle}</Sub>
        {/* {data.locked && <Lock>Coming soon</Lock>} */}
      </CardBody>
    </CardWrap>
  );
}

function Modal({ children, onClose }) {
  const { t } = useTranslation();
  return (
    <>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <Sheet
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        role="dialog"
        aria-modal="true"
        aria-label={t("cases.modal.ariaLabel")}
      >
        <Close onClick={onClose} aria-label={t("cases.modal.close")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </Close>
        {children}
      </Sheet>
    </>
  );
}

function CaseDetail({ data, prefersReduced, onReady }) {
  const { t } = useTranslation();
  const scrollRef = React.useRef(null);
  React.useEffect(() => { scrollRef.current?.focus(); }, []);

  // const onWheel = React.useCallback((e) => {
  //   if (!scrollRef.current) return;
  //   e.stopPropagation(); // block Lenis on window
  //   scrollRef.current.scrollTop += e.deltaY;
  // }, []);

  useInertialScroll(scrollRef);

  return (
    <Detail ref={scrollRef} tabIndex={0} layoutId={`card-${data.id}`} onLayoutAnimationComplete={onReady}>
      {/* top hero */}
      <DetailCover layoutId={`cover-${data.id}`} $hasImg={!!data.cover}>
        {data.cover && (
          <CoverImg
            src={data.cover}
            alt=""
            $fit={data.coverFit || "contain"}
            $position={data.coverPosition || "center"}
            $scale={data.coverScale || 1}
          />
        )}
      </DetailCover>

      {/* flowing content. this can now get very long.
         add as many paragraphs, screenshots, sections as you want below.
      */}
      <DetailBody>
        <Row>
          <TitleRow>
            <DetailTitle>{data.title}</DetailTitle>
            {data.year ? <Year>{data.year}</Year> : null}
          </TitleRow>
          {data.badges.length > 0 ? (
            <Badges>
              {data.badges.map((b) => (
                <Badge key={b}>{b}</Badge>
              ))}
            </Badges>
          ) : null}
        </Row>

        <DetailSub>{data.summary}</DetailSub>

        <Columns>
          {data.outcomes?.length ? (
            <InfoCard
              initial={prefersReduced ? false : { opacity: 0, y: 12 }}
              animate={prefersReduced ? false : { opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <InfoTitle>{t("cases.detail.outcomes")}</InfoTitle>
              <List>
                {data.outcomes.map((o, i) => (
                  <li key={i}>{o}</li>
                ))}
              </List>
            </InfoCard>
          ) : null}

          {data.stack?.length ? (
            <InfoCard
              initial={prefersReduced ? false : { opacity: 0, y: 12 }}
              animate={prefersReduced ? false : { opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05 }}
            >
              <InfoTitle>{t("cases.detail.stack")}</InfoTitle>
              <Tags>
                {data.stack.map((s, i) => (
                  <Tag key={i}>{s}</Tag>
                ))}
              </Tags>
            </InfoCard>
          ) : null}
        </Columns>

        <CTARow>
          <CTA href="/contact">{t("cases.cta.discuss")}</CTA>
          <CTASecondary href="/services">{t("cases.cta.services")}</CTASecondary>
        </CTARow>

        {/* long-form content starts here and will extend the scroll */}

        {data.sections?.map((section, idx) => (
          <React.Fragment key={idx}>
            <SectionBlock>
              <SectionHeading>
                {section.headingKey
                  ? t(`cases.detail.${section.headingKey}`)
                  : section.heading}
              </SectionHeading>
              <SectionText>{section.body}</SectionText>
              {section.list?.length ? (
                <List>
                  {section.list.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </List>
              ) : null}
            </SectionBlock>
            {section.media?.map((media, mediaIdx) => (
              <ScreenshotBlock
                key={`media-${idx}-${mediaIdx}`}
                style={
                  media.maxWidth
                    ? { maxWidth: media.maxWidth }
                    : media.variant === "small"
                    ? { maxWidth: "520px" }
                    : undefined
                }
              >
                <ScreenshotImg src={media.src} alt={media.alt || ""} />
                {(media.caption || media.credit) && (
                  <ScreenshotCaption>
                    {media.caption}
                    {media.credit ? (
                      <>
                        {" "}
                        <span style={{ opacity: 0.8 }}>{media.credit}</span>
                      </>
                    ) : null}
                  </ScreenshotCaption>
                )}
              </ScreenshotBlock>
            ))}
          </React.Fragment>
        ))}

        {/* you can keep adding more screenshots and paragraphs under this */}
      </DetailBody>
    </Detail>
  );
}

function IntegrationFallbackIcon({ icon, name }) {
  if (icon === "whatsapp") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.26-.46-2.39-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.07c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35Z" />
        <path d="M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.89 9.89-9.89 2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 0 1 2.89 6.99c0 5.45-4.44 9.89-9.88 9.89Zm8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.9a11.82 11.82 0 0 0-3.48-8.41Z" />
      </svg>
    );
  }

  if (icon === "instagram") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7.03.08c-1.28.06-2.15.26-2.91.56-.79.31-1.46.72-2.12 1.39C1.33 2.7.92 3.37.62 4.16.32 4.93.12 5.8.06 7.08 0 8.35-.01 8.76 0 12.02c.01 3.26.02 3.67.08 4.95.06 1.28.26 2.15.56 2.91.31.79.72 1.46 1.39 2.12.67.67 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.55 1.28.06 1.69.07 4.95.06 3.26-.01 3.67-.02 4.95-.08 1.28-.06 2.15-.27 2.91-.56.79-.31 1.46-.72 2.12-1.39.67-.67 1.07-1.34 1.38-2.13.3-.76.5-1.64.55-2.91.06-1.28.07-1.69.06-4.95-.01-3.26-.02-3.67-.08-4.95-.06-1.28-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.39-2.12C21.3 1.33 20.63.92 19.84.62 19.07.32 18.2.12 16.92.06 15.65.01 15.24 0 11.98 0 8.72.01 8.31.02 7.03.08Zm4.96 5.75a6.18 6.18 0 1 1 .02 12.36 6.18 6.18 0 0 1-.02-12.36Zm0 2.16a4 4 0 1 0 .02 8 4 4 0 0 0-.02-8Zm4.96-2.4a1.44 1.44 0 1 1 2.88-.01 1.44 1.44 0 0 1-2.88.01Z" />
      </svg>
    );
  }

  if (icon === "email") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="integration-email-icon">
        <path d="M3.75 6.75h16.5v10.5H3.75V6.75Z" />
        <path d="m4.5 7.5 7.5 5.65 7.5-5.65" />
      </svg>
    );
  }

  return (
    <span aria-hidden="true">
      {name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .slice(0, 2)}
    </span>
  );
}

function CaseStoryPage({ data }) {
  const { t } = useTranslation();
  const casePage = data.casePage || {};
  const sections = Array.isArray(data.sections) ? data.sections : [];
  const sectionBody = (key) => sections.find((section) => section.headingKey === key)?.body || "";
  const firstMedia = sections.flatMap((section) => section.media || [])[0];
  const visual = casePage.screenshotVisual || firstMedia;
  const storyVisual = casePage.storyVisual;
  const painBefore = casePage.painBefore || sectionBody("problem");
  const systemBuilt = casePage.systemBuilt || sectionBody("how");
  const result = casePage.result || sectionBody("impact") || data.summary;
  const primaryHref = casePage.primaryHref || data.externalUrl || "/contact";
  const primaryLabel = casePage.primaryCta || t("cases.cta.discuss");
  const isInternalPrimary = primaryHref.startsWith("/");
  const quote = casePage.quote;
  const isInstallatieBoutique = data.id === "installatieboutique";
  const isGemeenteAi = data.id === "gemeente-ai";
  const isSalenza = data.id === "salenza";
  const visualCaption = isInstallatieBoutique || isGemeenteAi || isSalenza ? null : casePage.workflowCaption || visual?.caption;
  const storyIntro = casePage.storyIntro;
  const workflowStory = casePage.workflowStory;
  const workflowStoryItems = Array.isArray(workflowStory?.items) ? workflowStory.items : [];
  const integrations = casePage.integrations;
  const integrationItems = Array.isArray(integrations?.items) ? integrations.items : [];
  const webApp = casePage.webApp;
  const webAppItems = Array.isArray(webApp?.items) ? webApp.items : [];
  const nexFoundation = casePage.nexFoundation;
  const nexFoundationItems = Array.isArray(nexFoundation?.items) ? nexFoundation.items : [];
  const contactCta = casePage.contactCta;

  return (
    <CasePage $shelter $theme={isGemeenteAi ? "gemeente" : isSalenza ? "salenza" : "default"}>
      <CasePageInner>
        <CaseBackLink as={Link} to="/work">
          {t("cases.detail.backToWork")}
        </CaseBackLink>

        <CaseHero>
          <CaseHeroCopy>
            <CaseHeroTitle>{data.title}</CaseHeroTitle>
            <CaseSubtitle>{data.subtitle}</CaseSubtitle>
            <CaseSummary>{data.summary}</CaseSummary>
            {data.year || data.badges.length > 0 ? (
              <CaseMetaRow>
                {data.year ? <Year>{data.year}</Year> : null}
                {data.badges.length > 0 ? (
                  <Badges>
                    {data.badges.map((badge) => (
                      <Badge key={badge}>{badge}</Badge>
                    ))}
                  </Badges>
                ) : null}
              </CaseMetaRow>
            ) : null}
            <CaseActionRow>
              <CasePrimaryCta
                as={isInternalPrimary ? Link : "a"}
                to={isInternalPrimary ? primaryHref : undefined}
                href={isInternalPrimary ? undefined : primaryHref}
                target={isInternalPrimary ? undefined : "_blank"}
                rel={isInternalPrimary ? undefined : "noopener noreferrer"}
              >
                {primaryLabel}
              </CasePrimaryCta>
              <CaseSecondaryCta as={Link} to="/work">
                {t("cases.detail.backToWork")}
              </CaseSecondaryCta>
            </CaseActionRow>
          </CaseHeroCopy>

          {isInstallatieBoutique || isGemeenteAi || isSalenza || visual ? (
            <CaseHeroMedia $workflow={isInstallatieBoutique || isSalenza}>
              {isInstallatieBoutique ? (
                <InstallatieWorkflowVisual />
              ) : isGemeenteAi ? (
                <GemeenteAiCaseVisual />
              ) : isSalenza ? (
                <SalenzaCaseVisual />
              ) : (
                <ScreenshotImg src={visual.src} alt={visual.alt || data.title} />
              )}
              {visualCaption ? <ScreenshotCaption>{visualCaption}</ScreenshotCaption> : null}
            </CaseHeroMedia>
          ) : null}
        </CaseHero>

        <CaseEngineGrid>
          <CaseEngineCard>
            <CaseEngineTitle>{t("cases.detail.painBefore")}</CaseEngineTitle>
            <CaseEngineText>{painBefore}</CaseEngineText>
          </CaseEngineCard>
          <CaseEngineCard>
            <CaseEngineTitle>{t("cases.detail.systemBuilt")}</CaseEngineTitle>
            <CaseEngineText>{systemBuilt}</CaseEngineText>
          </CaseEngineCard>
          <CaseEngineCard>
            <CaseEngineTitle>{t("cases.detail.result")}</CaseEngineTitle>
            <CaseEngineText>{result}</CaseEngineText>
            {data.outcomes?.length ? (
              <CaseOutcomeList>
                {data.outcomes.map((outcome) => (
                  <li key={outcome}>{outcome}</li>
                ))}
              </CaseOutcomeList>
            ) : null}
          </CaseEngineCard>
        </CaseEngineGrid>

        {storyIntro?.title ? (
          <CaseNarrativeBand>
            {storyIntro.eyebrow ? <CaseNarrativeEyebrow>{storyIntro.eyebrow}</CaseNarrativeEyebrow> : null}
            <CaseNarrativeTitle>{storyIntro.title}</CaseNarrativeTitle>
            {storyIntro.body ? <CaseNarrativeText>{storyIntro.body}</CaseNarrativeText> : null}
          </CaseNarrativeBand>
        ) : null}

        {workflowStoryItems.length ? (
          <CaseStorySection>
            <CaseSectionHeader>
              {workflowStory.eyebrow ? <CaseSectionEyebrow>{workflowStory.eyebrow}</CaseSectionEyebrow> : null}
              {workflowStory.title ? <CaseSectionTitle>{workflowStory.title}</CaseSectionTitle> : null}
              {workflowStory.lead ? <CaseSectionLead>{workflowStory.lead}</CaseSectionLead> : null}
            </CaseSectionHeader>
            <CaseStoryItemGrid>
              {workflowStoryItems.map((item, index) => (
                <CaseStoryItem key={`${item.title}-${index}`}>
                  <CaseStoryItemNumber>{String(index + 1).padStart(2, "0")}</CaseStoryItemNumber>
                  <CaseStoryItemTitle>{item.title}</CaseStoryItemTitle>
                  <CaseStoryItemText>{item.body}</CaseStoryItemText>
                </CaseStoryItem>
              ))}
            </CaseStoryItemGrid>
          </CaseStorySection>
        ) : null}

        {integrationItems.length ? (
          <CaseIntegrationsPanel aria-labelledby={`${data.id}-integrations-title`}>
            <CaseSectionHeader>
              {integrations.eyebrow ? <CaseSectionEyebrow>{integrations.eyebrow}</CaseSectionEyebrow> : null}
              {integrations.title ? (
                <CaseSectionTitle id={`${data.id}-integrations-title`}>{integrations.title}</CaseSectionTitle>
              ) : null}
              {integrations.body ? <CaseSectionLead>{integrations.body}</CaseSectionLead> : null}
            </CaseSectionHeader>
            <CaseIntegrationViewport>
              <CaseIntegrationTrack>
                {[...integrationItems, ...integrationItems].map((item, index) => (
                  <CaseIntegrationBanner
                    key={`${item.name}-${index}`}
                    aria-hidden={index >= integrationItems.length ? "true" : undefined}
                  >
                    <CaseIntegrationLogoSlot $logo={Boolean(item.logo)}>
                      {item.logo ? (
                        <CaseIntegrationLogo src={item.logo} alt="" loading="lazy" />
                      ) : (
                        <IntegrationFallbackIcon icon={item.icon} name={item.name} />
                      )}
                    </CaseIntegrationLogoSlot>
                    <CaseIntegrationText>
                      <strong>{item.name}</strong>
                      {item.type ? <span>{item.type}</span> : null}
                    </CaseIntegrationText>
                    {item.pending ? <CaseIntegrationPending>{item.pending}</CaseIntegrationPending> : null}
                  </CaseIntegrationBanner>
                ))}
              </CaseIntegrationTrack>
            </CaseIntegrationViewport>
          </CaseIntegrationsPanel>
        ) : null}

        {webApp?.title ? (
          <CaseWebAppPanel>
            <CaseWebAppCopy>
              {webApp.eyebrow ? <CaseSectionEyebrow>{webApp.eyebrow}</CaseSectionEyebrow> : null}
              <CaseSectionTitle>{webApp.title}</CaseSectionTitle>
              {webApp.body ? <CaseSectionLead>{webApp.body}</CaseSectionLead> : null}
            </CaseWebAppCopy>
            {webAppItems.length ? (
              <CaseWebAppList>
                {webAppItems.map((item) => (
                  <CaseWebAppItem key={item}>{item}</CaseWebAppItem>
                ))}
              </CaseWebAppList>
            ) : null}
          </CaseWebAppPanel>
        ) : null}

        {storyVisual?.src ? (
          <CaseStoryPanel $quote={isSalenza}>
            <CaseStoryMedia>
              <ScreenshotImg src={storyVisual.src} alt={storyVisual.alt || data.title} />
            </CaseStoryMedia>
            <CaseStoryCopy>
              <InfoTitle className={isSalenza ? "story-quote-title" : undefined}>
                {storyVisual.title || t("cases.detail.screenshotVisual")}
              </InfoTitle>
              {storyVisual.body ? <CaseEngineText>{storyVisual.body}</CaseEngineText> : null}
            </CaseStoryCopy>
          </CaseStoryPanel>
        ) : null}

        {quote?.text ? (
          <CaseQuote>
            <p>{quote.text}</p>
            {quote.by ? <span>{quote.by}</span> : null}
          </CaseQuote>
        ) : null}

        {nexFoundation?.title ? (
          <CaseNexPanel>
            <CaseNexHeader>
              {nexFoundation.logoSrc ? (
                <CaseNexLogoWrap>
                  <CaseNexLogo src={nexFoundation.logoSrc} alt={nexFoundation.logoAlt || "NEX"} />
                </CaseNexLogoWrap>
              ) : (
                <CaseNexLogoWrap>NEX</CaseNexLogoWrap>
              )}
              <CaseNexCopy>
                {nexFoundation.eyebrow ? <CaseSectionEyebrow>{nexFoundation.eyebrow}</CaseSectionEyebrow> : null}
                <CaseSectionTitle>{nexFoundation.title}</CaseSectionTitle>
                {nexFoundation.body ? <CaseSectionLead>{nexFoundation.body}</CaseSectionLead> : null}
                {nexFoundation.learnLabel && nexFoundation.learnHref ? (
                  <CaseNexLearnLink as={Link} to={nexFoundation.learnHref}>
                    {nexFoundation.learnLabel} →
                  </CaseNexLearnLink>
                ) : null}
              </CaseNexCopy>
            </CaseNexHeader>
            {nexFoundationItems.length ? (
              <CaseNexProcessGrid>
                {nexFoundationItems.map((item, index) => (
                  <CaseNexProcessItem key={`${item.label}-${index}`}>
                    <CaseNexProcessNumber>{String(index + 1).padStart(2, "0")}</CaseNexProcessNumber>
                    <CaseNexProcessText>
                      <strong>{item.label}</strong>
                      <span>{item.body}</span>
                    </CaseNexProcessText>
                  </CaseNexProcessItem>
                ))}
              </CaseNexProcessGrid>
            ) : null}
          </CaseNexPanel>
        ) : data.stack?.length ? (
          <CaseStackPanel>
            <InfoTitle>{t("cases.detail.stack")}</InfoTitle>
            <Tags>
              {data.stack.map((item) => (
                <Tag key={item}>{item}</Tag>
              ))}
            </Tags>
          </CaseStackPanel>
        ) : null}

        {contactCta?.title ? (
          <CaseContactPanel>
            <CaseContactCopy>
              {contactCta.eyebrow ? <CaseSectionEyebrow>{contactCta.eyebrow}</CaseSectionEyebrow> : null}
              <CaseContactTitle>{contactCta.title}</CaseContactTitle>
              {contactCta.body ? <CaseSectionLead>{contactCta.body}</CaseSectionLead> : null}
            </CaseContactCopy>
            <CaseContactActions>
              {contactCta.primaryLabel && contactCta.primaryHref ? (
                <CasePrimaryCta
                  as={contactCta.primaryHref.startsWith("/") ? Link : "a"}
                  to={contactCta.primaryHref.startsWith("/") ? contactCta.primaryHref : undefined}
                  href={contactCta.primaryHref.startsWith("/") ? undefined : contactCta.primaryHref}
                  target={contactCta.primaryHref.startsWith("/") ? undefined : "_blank"}
                  rel={contactCta.primaryHref.startsWith("/") ? undefined : "noopener noreferrer"}
                >
                  {contactCta.primaryLabel}
                </CasePrimaryCta>
              ) : null}
              {contactCta.secondaryLabel && contactCta.secondaryHref ? (
                <CaseSecondaryCta
                  as={contactCta.secondaryHref.startsWith("/") ? Link : "a"}
                  to={contactCta.secondaryHref.startsWith("/") ? contactCta.secondaryHref : undefined}
                  href={contactCta.secondaryHref.startsWith("/") ? undefined : contactCta.secondaryHref}
                  target={contactCta.secondaryHref.startsWith("/") ? undefined : "_blank"}
                  rel={contactCta.secondaryHref.startsWith("/") ? undefined : "noopener noreferrer"}
                >
                  {contactCta.secondaryLabel}
                </CaseSecondaryCta>
              ) : null}
            </CaseContactActions>
          </CaseContactPanel>
        ) : null}
      </CasePageInner>
    </CasePage>
  );
}

const workflowSteps = [
  {
    number: "01",
    title: { nl: "Klantvraag", en: "Customer request" },
    body: {
      nl: ["E-mail komt binnen", "Case wordt gestart"],
      en: ["Email comes in", "Case is created"],
    },
    icon: "mail",
    assetName: "klantvraag",
    x: 32,
    y: 54,
  },
  {
    number: "02",
    title: { nl: "AI concept", en: "AI draft" },
    body: {
      nl: ["Leest de context", "Schrijft concept"],
      en: ["Reads context", "Writes reply"],
    },
    icon: "spark",
    assetName: "ai-concept",
    x: 310,
    y: 54,
  },
  {
    number: "03",
    title: { nl: "WhatsApp akkoord", en: "WhatsApp approval" },
    body: {
      nl: ["Stuurt voorstel", "Wacht op akkoord"],
      en: ["Sends proposal", "Waits for approval"],
    },
    icon: "whatsapp",
    assetName: "whatsapp-akkoord",
    x: 588,
    y: 54,
    longTitle: true,
  },
  {
    number: "04",
    title: { nl: "AI leert", en: "AI learns" },
    body: {
      nl: ["Slaat feedback op", "Herkent herhaling"],
      en: ["Stores feedback", "Spots repeats"],
    },
    icon: "learn",
    assetName: "ai-leert",
    x: 32,
    y: 368,
  },
  {
    number: "05",
    title: { nl: "AI plant", en: "AI plans" },
    body: {
      nl: ["Stelt tijden voor", "Boekt na akkoord"],
      en: ["Suggests times", "Books after approval"],
    },
    icon: "calendar",
    assetName: "planning",
    aiBadge: true,
    x: 310,
    y: 368,
  },
  {
    number: "06",
    title: { nl: "AI offerte", en: "AI quote" },
    body: {
      nl: ["Vult casegegevens", "Zet PDF klaar"],
      en: ["Fills case data", "Prepares PDF"],
    },
    icon: "quote",
    assetName: "offerte",
    aiBadge: true,
    badgeX: 144,
    badgeY: 70,
    x: 588,
    y: 368,
  },
];

const workflowIconBasePath = "/work/installatie-workflow-icons";
const workflowTravelDurationMs = 12500;
const workflowEndHoldDurationMs = 1800;
const workflowMotionDurationMs = workflowTravelDurationMs + workflowEndHoldDurationMs;
const workflowMaxRouteProgress = 0.995;
const workflowPulseRatio = 0.105;
const workflowCardSize = { width: 240, height: 238 };
const workflowRoutePath = [
  "M152 173",
  "H430",
  "H708",
  "V304",
  "Q708 324 688 324",
  "H184",
  "Q152 324 152 356",
  "V487",
  "H430",
  "H708",
].join(" ");

function findWorkflowStepAtPoint(point) {
  if (!point) return -1;

  return workflowSteps.findIndex((step) => (
    point.x >= step.x &&
    point.x <= step.x + workflowCardSize.width &&
    point.y >= step.y &&
    point.y <= step.y + workflowCardSize.height
  ));
}

function findActiveWorkflowStep(path, head, routeLength, pulseLength) {
  if (!path || routeLength <= 0) return [];

  const sampleDistances = [
    head,
    (head - pulseLength * 0.35 + routeLength) % routeLength,
    (head - pulseLength * 0.7 + routeLength) % routeLength,
  ];

  const activeIndex = sampleDistances
    .map((distance) => findWorkflowStepAtPoint(path.getPointAtLength(distance)))
    .find((index) => index !== -1);

  return activeIndex === -1 ? [] : [activeIndex];
}

function useWorkflowPathMotion({ routePathRef, liveLineRef, setActiveStepIndexes, reducedMotion }) {
  useEffect(() => {
    const routePath = routePathRef.current;
    const liveLine = liveLineRef.current;

    if (!routePath || !liveLine) {
      return undefined;
    }

    if (
      reducedMotion ||
      typeof routePath.getTotalLength !== "function" ||
      typeof routePath.getPointAtLength !== "function" ||
      typeof window.requestAnimationFrame !== "function" ||
      typeof window.cancelAnimationFrame !== "function"
    ) {
      liveLine.setAttribute("d", "");
      setActiveStepIndexes([]);
      return undefined;
    }

    const routeLength = routePath.getTotalLength();
    const pulseLength = routeLength * workflowPulseRatio;

    let animationFrame = null;
    let activeKeyRef = "";

    const animate = (now) => {
      const elapsed = now % workflowMotionDurationMs;
      const progress = elapsed >= workflowTravelDurationMs
        ? workflowMaxRouteProgress
        : (elapsed / workflowTravelDurationMs) * workflowMaxRouteProgress;
      const head = progress * routeLength;
      liveLine.style.strokeDashoffset = `${-progress * 1000}`;

      const activeIndexes = findActiveWorkflowStep(routePath, head, routeLength, pulseLength);
      const activeKey = activeIndexes.join(",");
      if (activeKey !== activeKeyRef) {
        activeKeyRef = activeKey;
        setActiveStepIndexes(activeIndexes);
      }

      animationFrame = window.requestAnimationFrame(animate);
    };

    animationFrame = window.requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [routePathRef, liveLineRef, reducedMotion, setActiveStepIndexes]);
}

function useWorkflowIconAssets() {
  const [assets, setAssets] = useState({});

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.Image !== "function") {
      return undefined;
    }

    let cancelled = false;

    [...workflowSteps.map((step) => step.assetName), "ai-badge"].forEach((assetName) => {
      const candidates = [
        `${workflowIconBasePath}/${assetName}.svg`,
        `${workflowIconBasePath}/${assetName}.png`,
      ];

      const loadCandidate = (index) => {
        if (cancelled || index >= candidates.length) return;

        const img = new window.Image();
        img.onload = () => {
          if (!cancelled) {
            setAssets((current) => ({
              ...current,
              [assetName]: candidates[index],
            }));
          }
        };
        img.onerror = () => loadCandidate(index + 1);
        img.src = candidates[index];
      };

      loadCandidate(0);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return assets;
}

function WorkflowAiBadge({ src, step }) {
  if (!src) return null;

  return (
    <image
      className="workflow-ai-badge-asset"
      href={src}
      x={step.badgeX || 150}
      y={step.badgeY || 70}
      width="24"
      height="24"
      preserveAspectRatio="xMidYMid meet"
    />
  );
}

function WorkflowIconSlot({ step, assetSrc }) {
  if (assetSrc) {
    return (
      <image
        className="workflow-icon-asset"
        href={assetSrc}
        x="89"
        y="68"
        width="62"
        height="62"
        preserveAspectRatio="xMidYMid meet"
      />
    );
  }

  return (
    <g className="workflow-icon" transform="translate(80 64) scale(0.66)">
      <WorkflowIcon type={step.icon} />
    </g>
  );
}

function WorkflowIcon({ type }) {
  if (type === "mail") {
    return (
      <>
        <rect x="21" y="34" width="72" height="48" rx="8" />
        <path className="workflow-envelope-fill" d="M27 42L57 62L87 42Z" />
        <path d="M24 42L57 65L90 42" />
        <circle className="workflow-cyan-dot" cx="92" cy="31" r="7" />
      </>
    );
  }

  if (type === "spark") {
    return (
      <>
        <path d="M58 20C64 43 73 52 96 58C73 64 64 73 58 96C52 73 43 64 20 58C43 52 52 43 58 20Z" />
        <path d="M92 18L96 29L108 34L96 39L92 50L87 39L76 34L87 29Z" />
      </>
    );
  }

  if (type === "whatsapp") {
    return (
      <>
        <path d="M60 21C39 21 23 37 23 57C23 67 27 76 34 83L31 98L47 92C51 94 55 95 60 95C81 95 97 79 97 57C97 37 81 21 60 21Z" />
        <path className="workflow-icon-fill" d="M49 42C50 58 63 71 79 75C84 76 90 70 86 67L79 62C77 61 75 62 73 65C66 62 59 55 56 48C59 46 60 44 59 42L55 35C53 32 48 37 49 42Z" />
      </>
    );
  }

  if (type === "learn") {
    return (
      <>
        <path d="M34 36C34 27 86 27 86 36C86 45 34 45 34 36Z" />
        <path d="M34 36V74C34 83 86 83 86 74V36" />
        <path d="M34 55C34 64 86 64 86 55" />
        <path d="M34 74C34 83 86 83 86 74" />
        <circle className="workflow-cyan-stroke" cx="86" cy="82" r="15" />
        <path className="workflow-cyan-stroke" d="M78 82L84 88L95 76" />
      </>
    );
  }

  if (type === "calendar") {
    return (
      <>
        <rect x="25" y="30" width="70" height="66" rx="10" />
        <path d="M25 49H95" />
        <path d="M42 20V38M78 20V38" />
        <circle cx="43" cy="65" r="3" />
        <circle cx="60" cy="65" r="3" />
        <circle cx="77" cy="65" r="3" />
        <circle cx="43" cy="82" r="3" />
        <circle cx="60" cy="82" r="3" />
        <circle cx="77" cy="82" r="3" />
      </>
    );
  }

  return (
    <>
      <path d="M38 22H72L88 38V96H38Z" />
      <path d="M72 22V38H88" />
      <path d="M52 59H69M50 72H62M50 83H69" />
      <path d="M77 62C72 58 63 60 60 68C57 77 64 86 74 83" />
      <path d="M55 69H76M55 78H73" />
    </>
  );
}

function InstallatieWorkflowVisual() {
  const { i18n } = useTranslation();
  const isNl = i18n.language?.startsWith("nl");
  const iconAssets = useWorkflowIconAssets();
  const prefersReducedMotion = usePrefersReducedMotion();
  const routePathRef = useRef(null);
  const liveLineRef = useRef(null);
  const [activeStepIndexes, setActiveStepIndexes] = useState([]);
  const localizedSteps = workflowSteps.map((step) => ({
    ...step,
    title: isNl ? step.title.nl : step.title.en,
    body: isNl ? step.body.nl : step.body.en,
  }));

  useWorkflowPathMotion({
    routePathRef,
    liveLineRef,
    setActiveStepIndexes,
    reducedMotion: prefersReducedMotion,
  });

  return (
    <WorkflowVisualWrap data-installatie-workflow-visual>
      <WorkflowSvg
        viewBox="0 0 860 660"
        role="img"
        aria-label={isNl ? "AI-proces van klantvraag naar offerte" : "AI process from customer request to quote"}
      >
        <defs>
          <radialGradient id="workflow-core-glow" cx="50%" cy="50%" r="56%">
            <stop offset="0%" stopColor="rgba(190,231,241,0.30)" />
            <stop offset="70%" stopColor="rgba(190,231,241,0.04)" />
            <stop offset="100%" stopColor="rgba(190,231,241,0)" />
          </radialGradient>
          <filter id="workflow-soft-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g className="workflow-lines" aria-hidden="true">
          <path
            ref={routePathRef}
            className="workflow-line-base"
            d={workflowRoutePath}
          />
          <path
            ref={liveLineRef}
            className="workflow-line-live"
            d={workflowRoutePath}
            pathLength="1000"
          />
        </g>

        {localizedSteps.map((step, index) => (
          <g
            key={step.number}
            className={`workflow-step workflow-step-${index + 1}${activeStepIndexes.includes(index) ? " is-active" : ""}`}
            transform={`translate(${step.x} ${step.y})`}
          >
            <rect className="workflow-card-glow" x="-16" y="-16" width="272" height="270" rx="38" />
            <rect className="workflow-card" width="240" height="238" rx="30" />
            <rect className="workflow-card-active-edge" width="240" height="238" rx="30" />
            <circle className="workflow-step-number-ring" cx="120" cy="34" r="23" />
            <text className="workflow-step-number" x="120" y="40">{step.number}</text>
            {step.aiBadge ? <WorkflowAiBadge src={iconAssets["ai-badge"]} step={step} /> : null}
            <WorkflowIconSlot step={step} assetSrc={iconAssets[step.assetName]} />
            <text className={step.longTitle ? "workflow-title long" : "workflow-title"} x="120" y="166">
              {step.title}
            </text>
            {step.body.map((line, lineIndex) => (
              <g key={line} className="workflow-bullet-row">
                <circle className="workflow-bullet-dot" cx="52" cy={193 + lineIndex * 21} r="3.2" />
                <text
                  className="workflow-body workflow-body-bullet"
                  x="65"
                  y={198 + lineIndex * 21}
                >
                  {line}
                </text>
              </g>
            ))}
          </g>
        ))}
      </WorkflowSvg>
    </WorkflowVisualWrap>
  );
}

function GemeenteAiCaseVisual() {
  const { i18n } = useTranslation();
  const isNl = i18n.language?.startsWith("nl");

  const sourceItems = isNl
    ? ["Gemeente website", "Nieuws", "Beleid", "Rijksoverheid"]
    : ["Municipal website", "News", "Policy", "Central government"];

  const outputItems = isNl
    ? [
        { title: "Chat + spraak", body: ["Direct antwoord", "in elke taal"] },
        { title: "Dashboard", body: ["Gesprekken, bronnen", "en kwaliteit"] },
        { title: "Overdracht", body: ["Melding zodra", "mens nodig is"] },
        { title: "Medewerker neemt over", body: ["Team ziet context", "en chat mee"] },
      ]
    : [
        { title: "Chat + voice", body: ["Direct answers", "in any language"] },
        { title: "Dashboard", body: ["Conversations, sources", "and quality"] },
        { title: "Transfer alert", body: ["Signal when", "a human is needed"] },
        { title: "Staff takeover", body: ["Team sees context", "and joins in"] },
      ];

  const metrics = isNl
    ? [
        { value: "24/7", label: "beschikbaar" },
        { value: "40+", label: "talen" },
        { value: "AVG", label: "vriendelijk" },
        { value: "GDPR", label: "klaar" },
      ]
    : [
        { value: "24/7", label: "available" },
        { value: "40+", label: "languages" },
        { value: "AVG", label: "proof" },
        { value: "GDPR", label: "ready" },
      ];

  const sourceCards = sourceItems.map((label, index) => ({
    label,
    x: 72,
    y: 168 + index * 78,
    width: 260,
    height: 62,
  }));

  const outputCards = outputItems.map((item, index) => ({
    ...item,
    x: 640,
    y: 62 + index * 118,
    width: 330,
    height: 104,
  }));

  const sourceEngineAnchors = [
    { x: 414, y: 258 },
    { x: 394, y: 298 },
    { x: 394, y: 342 },
    { x: 414, y: 382 },
  ];
  const engineOutputAnchors = [
    { x: 588, y: 258 },
    { x: 612, y: 298 },
    { x: 612, y: 342 },
    { x: 588, y: 382 },
  ];

  const routePaths = [
    ...sourceCards.map((card, index) => {
      const startX = card.x + card.width;
      const startY = card.y + card.height / 2;
      const end = sourceEngineAnchors[index];
      return `M${startX} ${startY} C374 ${startY} 382 ${end.y} ${end.x} ${end.y}`;
    }),
    ...outputCards.map((card, index) => {
      const endX = card.x;
      const endY = card.y + card.height / 2;
      const start = engineOutputAnchors[index];
      return `M${start.x} ${start.y} C636 ${start.y} 620 ${endY} ${endX} ${endY}`;
    }),
  ];

  return (
    <GemeenteVisualWrap role="img" aria-label={isNl ? "Gemeente AI platform flow" : "Gemeente AI platform flow"}>
      <GemeenteFlowSvg viewBox="0 0 1000 720" aria-hidden>
        <defs>
          <linearGradient id="gemeenteFlowStroke" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="rgba(19, 184, 244, 0)" />
            <stop offset="44%" stopColor="rgba(35, 211, 255, 0.98)" />
            <stop offset="100%" stopColor="rgba(56, 142, 255, 0.86)" />
          </linearGradient>
          <filter id="gemeenteFlowGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g className="gemeente-source-panel">
          <rect className="gemeente-panel" x="40" y="102" width="330" height="390" rx="28" />
          {sourceCards.map((card) => (
            <rect
              key={card.label}
              className="gemeente-source-card"
              x={card.x}
              y={card.y}
              width={card.width}
              height={card.height}
              rx="13"
            />
          ))}
        </g>

        <g className="gemeente-output-panel">
          {outputCards.map((card) => (
            <rect
              key={card.title}
              className="gemeente-output-card"
              x={card.x}
              y={card.y}
              width={card.width}
              height={card.height}
              rx="16"
            />
          ))}
        </g>

        <g className="gemeente-metric-panel">
          {metrics.map((metric, index) => {
            const x = 40 + index * 235;
            return (
              <rect
                key={`${metric.value}-${metric.label}`}
                className="gemeente-metric-card"
                x={x}
                y="594"
                width="190"
                height="76"
                rx="16"
              />
            );
          })}
        </g>

        {routePaths.map((path, index) => (
          <React.Fragment key={path}>
            <path className="gemeente-route-base" d={path} />
            <path
              className="gemeente-route-live"
              d={path}
              style={{ animationDelay: `${index * 0.2}s` }}
            />
          </React.Fragment>
        ))}

        <g className="gemeente-engine-node">
          <circle className="gemeente-engine-glow" cx="500" cy="320" r="132" />
          <circle className="gemeente-engine-circle" cx="500" cy="320" r="114" />
          <text className="gemeente-engine-kicker" x="500" y="272">{isNl ? "GEMEENTE AI" : "MUNICIPAL AI"}</text>
          <text className="gemeente-engine-title" x="500" y="318">{isNl ? "Assistent" : "Assistant"}</text>
          <text className="gemeente-engine-body" x="500" y="354">
            <tspan x="500" dy="0">{isNl ? "Bronbewust," : "Source-aware,"}</tspan>
            <tspan x="500" dy="25">{isNl ? "meertalig, met" : "multilingual, with"}</tspan>
            <tspan x="500" dy="25">{isNl ? "overdracht." : "transfer."}</tspan>
          </text>
        </g>

        <g className="gemeente-source-labels">
          <text className="gemeente-kicker" x="72" y="146">
            {isNl ? "PUBLIEKE DATA" : "PUBLIC DATA"}
          </text>
          {sourceCards.map((card) => (
            <text className="gemeente-source-text" key={card.label} x={card.x + 22} y={card.y + 39}>
              {card.label}
            </text>
          ))}
        </g>

        <g className="gemeente-output-labels">
          {outputCards.map((item) => {
            return (
              <g key={item.title}>
                <text className="gemeente-output-title" x={item.x + 26} y={item.y + 38}>{item.title}</text>
                <text className="gemeente-output-body" x={item.x + 26} y={item.y + 67}>
                  {item.body.map((line, lineIndex) => (
                    <tspan key={line} x={item.x + 26} dy={lineIndex === 0 ? 0 : 23}>{line}</tspan>
                  ))}
                </text>
              </g>
            );
          })}
        </g>

        <g className="gemeente-metric-labels">
          {metrics.map((metric, index) => {
            const x = 40 + index * 235;
            return (
              <g key={`${metric.value}-${metric.label}`}>
                <text className="gemeente-metric-value" x={x + 95} y="628">{metric.value}</text>
                <text className="gemeente-metric-label" x={x + 95} y="654">{metric.label}</text>
              </g>
            );
          })}
        </g>
      </GemeenteFlowSvg>
    </GemeenteVisualWrap>
  );
}

function SalenzaCaseVisual() {
  const { i18n } = useTranslation();
  const isNl = i18n.language?.startsWith("nl");

  const channelItems = isNl
    ? [
        { title: "WhatsApp", body: "Klantvraag", icon: "whatsapp" },
        { title: "Instagram", body: "DM of reactie", icon: "instagram" },
        { title: "E-mail", body: "Klantvraag", icon: "email" },
      ]
    : [
        { title: "WhatsApp", body: "Customer question", icon: "whatsapp" },
        { title: "Instagram", body: "DM or comment", icon: "instagram" },
        { title: "Email", body: "Customer question", icon: "email" },
      ];

  const actionItems = isNl
    ? [
        { title: "Antwoord", body: "Prijzen en vragen" },
        { title: "Boeking", body: "Maken of wijzigen" },
        { title: "Intake", body: "Vooraf verzamelen" },
        { title: "Mens-modus", body: "Team neemt over" },
      ]
    : [
        { title: "Answer", body: "Prices and questions" },
        { title: "Booking", body: "Create or move" },
        { title: "Intake", body: "Collect upfront" },
        { title: "Human mode", body: "Team takes over" },
      ];

  const platformItems = isNl
    ? [
        { title: "Treatwell", body: "Boekingen" },
        { title: "Salonized", body: "Salonsoftware" },
        { title: "Clinic Minds", body: "Klinieken" },
        { title: "Meer koppelingen", body: ["Salonkee · Fresha", "Google Calendar + meer"] },
      ]
    : [
        { title: "Treatwell", body: "Bookings" },
        { title: "Salonized", body: "Salon software" },
        { title: "Clinic Minds", body: "Clinics" },
        { title: "More integrations", body: ["Salonkee · Fresha", "Google Calendar + more"] },
      ];

  const channelCards = channelItems.map((item, index) => ({
    ...item,
    x: 80,
    y: 142 + index * 132,
    width: 300,
    height: 96,
  }));

  const platformCards = platformItems.map((item, index) => ({
    ...item,
    x: 646,
    y: 122 + index * 100,
    width: 292,
    height: 82,
  }));

  const actionCards = actionItems.map((item, index) => ({
    ...item,
    x: 56 + index * 229,
    y: 590,
    width: 206,
    height: 74,
  }));

  const core = { x: 512, y: 322, r: 82 };
  const leftAnchors = [
    { x: 454, y: 264 },
    { x: core.x - core.r, y: core.y },
    { x: 454, y: 380 },
  ];

  const rightAnchors = [
    { x: 574, y: 268 },
    { x: core.x + core.r, y: 304 },
    { x: core.x + core.r, y: 340 },
    { x: 574, y: 376 },
  ];

  const routePaths = [
    ...channelCards.map((card, index) => {
      const startX = card.x + card.width;
      const startY = card.y + card.height / 2;
      const end = leftAnchors[index];
      return `M${startX} ${startY} C408 ${startY} 410 ${end.y} ${end.x} ${end.y}`;
    }),
    ...platformCards.map((card, index) => {
      const endX = card.x;
      const endY = card.y + card.height / 2;
      const start = rightAnchors[index];
      return `M${start.x} ${start.y} C612 ${start.y} 618 ${endY} ${endX} ${endY}`;
    }),
    `M${core.x} ${core.y + core.r} C${core.x} 454 ${core.x} 494 ${core.x} 534`,
    "M158 534H849",
    ...actionCards.map((card) => {
      const x = card.x + card.width / 2;
      return `M${x} 534V${card.y}`;
    }),
  ];

  const renderChannelIcon = (icon, x, y) => {
    if (icon === "whatsapp") {
      return (
        <image
          className="salenza-channel-logo"
          href="/work/salenza/channels/whatsapp.png"
          x={x}
          y={y}
          width="56"
          height="56"
          preserveAspectRatio="xMidYMid meet"
        />
      );
    }

    if (icon === "instagram") {
      return (
        <image
          className="salenza-channel-logo"
          href="/work/salenza/channels/instagram.png"
          x={x}
          y={y}
          width="56"
          height="56"
          preserveAspectRatio="xMidYMid meet"
        />
      );
    }

    return (
      <g className="salenza-channel-icon salenza-channel-icon-email" transform={`translate(${x} ${y})`}>
        <rect x="9" y="14" width="42" height="31" rx="6" />
        <path d="M12.4 18.1 30 31.2 47.6 18.1" />
      </g>
    );
  };

  const renderTextLines = ({ className, x, y, lines, lineGap = 16, anchor = "start" }) => {
    const textLines = Array.isArray(lines) ? lines : [lines];

    return (
      <text className={className} x={x} y={y} textAnchor={anchor}>
        {textLines.map((line, index) => (
          <tspan key={line} x={x} dy={index === 0 ? 0 : lineGap}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  return (
    <SalenzaVisualWrap
      className="salenza-case-visual"
      role="img"
      aria-label={isNl ? "Salenza AI-receptionist flow" : "Salenza AI receptionist flow"}
    >
      <SalenzaFlowSvg viewBox="0 0 1000 720" aria-hidden>
        <defs>
          <linearGradient id="salenzaFlowStroke" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="rgba(20, 184, 122, 0)" />
            <stop offset="42%" stopColor="rgba(80, 255, 183, 0.98)" />
            <stop offset="100%" stopColor="rgba(219, 255, 239, 0.9)" />
          </linearGradient>
          <filter id="salenzaFlowGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {channelCards.map((card) => (
          <g key={card.title} className="salenza-card salenza-channel-card">
            <rect x={card.x} y={card.y} width={card.width} height={card.height} rx="18" />
          </g>
        ))}

        {platformCards.map((card) => (
          <g key={card.title} className="salenza-card salenza-platform-card">
            <rect x={card.x} y={card.y} width={card.width} height={card.height} rx="18" />
          </g>
        ))}

        {actionCards.map((card) => (
          <g key={card.title} className="salenza-card salenza-action-card">
            <rect x={card.x} y={card.y} width={card.width} height={card.height} rx="31" />
          </g>
        ))}

        {routePaths.map((path, index) => (
          <React.Fragment key={path}>
            <path className="salenza-route-base" d={path} pathLength="1" />
            <path
              className="salenza-route-live"
              d={path}
              pathLength="1"
              style={{ animationDelay: `${index * 0.22}s` }}
            />
          </React.Fragment>
        ))}

        <g className="salenza-core">
          <circle className="salenza-core-halo" cx={core.x} cy={core.y} r="96" />
          <circle className="salenza-core-ring" cx={core.x} cy={core.y} r={core.r} />
          <path className="salenza-core-orbit" d={`M${core.x - 58} ${core.y - 66} C${core.x - 10} ${core.y - 92} ${core.x + 40} ${core.y - 88} ${core.x + 66} ${core.y - 46}`} />
          <path className="salenza-core-orbit salenza-core-orbit-bottom" d={`M${core.x - 68} ${core.y + 42} C${core.x - 26} ${core.y + 84} ${core.x + 36} ${core.y + 82} ${core.x + 68} ${core.y + 36}`} />
          <text className="salenza-core-title" x={core.x} y={core.y - 2}>Salenza</text>
          <text className="salenza-core-subtitle" x={core.x} y={core.y + 28}>AI-RECEPTIONIST</text>
        </g>

        <g className="salenza-channel-labels">
          <text className="salenza-kicker" x="230" y="104">{isNl ? "KANALEN" : "CHANNELS"}</text>
          {channelCards.map((card) => (
            <g key={card.title}>
              {renderChannelIcon(card.icon, card.x + 30, card.y + 20)}
              <text className="salenza-card-title" x={card.x + 98} y={card.y + 42}>{card.title}</text>
              <text className="salenza-card-body" x={card.x + 98} y={card.y + 67}>{card.body}</text>
            </g>
          ))}
        </g>

        <g className="salenza-platform-labels">
          <text className="salenza-kicker salenza-kicker-platforms" x="792" y="104">
            {isNl ? "BOEKINGSSYSTEMEN" : "BOOKING PLATFORMS"}
          </text>
          {platformCards.map((card) => (
            <g key={card.title}>
              <text className="salenza-card-title salenza-platform-title" x={card.x + 34} y={card.y + 35}>{card.title}</text>
              {renderTextLines({
                className: "salenza-card-body salenza-platform-body",
                x: card.x + 34,
                y: card.y + 58,
                lines: card.body,
                lineGap: 14,
              })}
            </g>
          ))}
        </g>

        <g className="salenza-action-labels">
          <text className="salenza-kicker" x="500" y="558">{isNl ? "ACTIES" : "ACTIONS"}</text>
          {actionCards.map((card) => (
            <g key={card.title}>
              <text className="salenza-action-title" x={card.x + card.width / 2} y={card.y + 30}>{card.title}</text>
              {renderTextLines({
                className: "salenza-action-body",
                x: card.x + card.width / 2,
                y: card.y + 54,
                lines: card.body,
                anchor: "middle",
              })}
            </g>
          ))}
        </g>
      </SalenzaFlowSvg>
    </SalenzaVisualWrap>
  );
}


/* ========================= Hooks ========================= */

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }

    mq.addListener?.(onChange);
    return () => mq.removeListener?.(onChange);
  }, []);
  return reduced;
}

/* ========================= Styled ========================= */

const Page = styled.div.attrs((p) => (p.$shelter ? { "data-contrast-shelter": "" } : {}))`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: clamp(28px, 4vw, 42px);
  padding: clamp(72px, 8vh, 112px) 0 clamp(84px, 10vh, 128px);
  color: #fff;
  position: relative;
`;

const CasePage = styled(Page)`
  --case-accent-rgb: 214, 181, 104;
  --case-accent: rgba(var(--case-accent-rgb), 0.9);
  --case-accent-border: rgba(var(--case-accent-rgb), 0.28);
  --case-accent-border-strong: rgba(var(--case-accent-rgb), 0.72);
  --case-accent-bg: rgba(var(--case-accent-rgb), 0.08);

  ${(p) =>
    p.$theme === "gemeente" &&
    `
      --case-accent-rgb: 35, 211, 255;
      --case-accent: rgba(var(--case-accent-rgb), 0.92);
      --case-accent-border: rgba(var(--case-accent-rgb), 0.28);
      --case-accent-border-strong: rgba(var(--case-accent-rgb), 0.72);
      --case-accent-bg: rgba(var(--case-accent-rgb), 0.08);
    `}

  ${(p) =>
    p.$theme === "salenza" &&
    `
      --case-accent-rgb: 39, 224, 147;
      --case-accent: rgba(var(--case-accent-rgb), 0.92);
      --case-accent-border: rgba(var(--case-accent-rgb), 0.28);
      --case-accent-border-strong: rgba(var(--case-accent-rgb), 0.72);
      --case-accent-bg: rgba(var(--case-accent-rgb), 0.08);
    `}

  padding-top: clamp(84px, 9vh, 118px);
`;

const CasePageInner = styled.div`
  width: min(calc(100% - (var(--content-gutter) * 2)), var(--content-wide-width));
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: grid;
  gap: clamp(24px, 4vw, 42px);
`;

const CaseNotFound = styled.div`
  width: min(calc(100% - (var(--content-gutter) * 2)), 760px);
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: start;
  gap: 16px;
`;

const CaseBackLink = styled(Link)`
  width: fit-content;
  color: rgba(255, 255, 255, 0.72);
  text-decoration: none;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  &::before {
    content: "←";
    margin-right: 9px;
  }

  &:hover {
    color: #fff;
  }
`;

const CaseHero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(420px, 0.86fr);
  gap: clamp(24px, 4vw, 54px);
  align-items: start;

  @media (max-width: 1280px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
`;

const CaseHeroCopy = styled.div`
  min-width: 0;
  max-width: 840px;
  display: grid;
  justify-items: start;
  gap: 16px;
`;

const CaseHeroTitle = styled.h1`
  margin: 0;
  max-width: 100%;
  font-size: clamp(42px, 4.8vw, 68px);
  line-height: 1.02;
  letter-spacing: 0;
  text-wrap: balance;
  overflow-wrap: normal;
  word-break: normal;

  @media (max-width: 520px) {
    font-size: clamp(29px, 8.2vw, 34px);
    line-height: 1.02;
  }
`;

const CaseSubtitle = styled.p`
  margin: 0;
  max-width: 760px;
  font-size: clamp(22px, 3vw, 34px);
  line-height: 1.16;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.92);
`;

const CaseSummary = styled.p`
  margin: 0;
  max-width: 760px;
  font-size: 18px;
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.78);
`;

const CaseMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const CaseActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 8px;
`;

const CasePrimaryCta = styled.a`
  min-height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  border-radius: 999px;
  background: #fff;
  color: #050505;
  text-decoration: none;
  font-size: 14px;
  font-weight: 900;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.28);
`;

const CaseSecondaryCta = styled.a`
  min-height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.045);
  color: #fff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 900;
`;

const CaseHeroMedia = styled.div`
  overflow: hidden;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02)),
    rgba(8, 8, 8, 0.56);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 30px 80px rgba(0, 0, 0, 0.36);

  ${(p) =>
    p.$workflow &&
    `
      padding: 0;
      border: 0;
      background: transparent;
      box-shadow: none;
    `}
`;

const WorkflowVisualWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 86 / 66;
  min-height: 470px;
  overflow: hidden;
  border-radius: 30px;
  border: 1px solid rgba(214, 181, 104, 0.26);
  background:
    radial-gradient(78% 64% at 54% 44%, rgba(214, 181, 104, 0.12), rgba(214, 181, 104, 0) 70%),
    radial-gradient(56% 58% at 86% 22%, rgba(190, 231, 241, 0.075), rgba(190, 231, 241, 0) 68%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.016)),
    rgba(3, 4, 4, 0.9);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    inset 0 0 42px rgba(214, 181, 104, 0.045),
    0 28px 70px rgba(0, 0, 0, 0.34);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(90deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px),
      linear-gradient(180deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
    background-size: 82px 82px;
    width: 100%;
    mix-blend-mode: screen;
    opacity: 0.055;
  }

  @media (max-width: 620px) {
    min-height: 360px;
    border-radius: 22px;
  }
`;

const WorkflowSvg = styled.svg`
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;

  .workflow-line-base {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke: rgba(214, 181, 104, 0.30);
    stroke-width: 4;
  }

  .workflow-line-live {
    fill: none;
    stroke: rgba(190, 231, 241, 0.98);
    stroke-width: 9;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 105 895;
    stroke-dashoffset: 0;
    filter: url(#workflow-soft-glow);
  }

  .workflow-card-glow {
    fill: url(#workflow-core-glow);
    opacity: 0;
    filter: url(#workflow-soft-glow);
    transform-box: fill-box;
    transform-origin: center;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }

  .workflow-card {
    fill: #050606;
    stroke: rgba(214, 181, 104, 0.66);
    stroke-width: 1.6;
  }

  .workflow-card-active-edge {
    fill: none;
    opacity: 0;
    stroke: rgba(190, 231, 241, 0.96);
    stroke-width: 2.6;
    filter: url(#workflow-soft-glow);
    transition: opacity 0.16s ease;
  }

  .workflow-step.is-active .workflow-card-glow {
    opacity: 0.68;
    transform: scale(1.016);
  }

  .workflow-step.is-active .workflow-card-active-edge {
    opacity: 1;
  }

  .workflow-step-number-ring {
    fill: rgba(5, 5, 5, 0.82);
    stroke: rgba(214, 181, 104, 0.86);
    stroke-width: 2;
  }

  .workflow-step-number,
  .workflow-title,
  .workflow-body {
    text-anchor: middle;
    fill: rgba(255, 255, 255, 0.96);
    font-family: var(--font-body);
    letter-spacing: 0;
  }

  .workflow-step-number {
    font-size: 17px;
    font-weight: 900;
  }

  .workflow-title {
    font-size: 26px;
    font-weight: 900;
  }

  .workflow-title.long {
    font-size: 21px;
  }

  .workflow-body {
    fill: rgba(255, 255, 255, 0.74);
    font-size: 15.5px;
  }

  .workflow-body-bullet {
    text-anchor: start;
  }

  .workflow-bullet-dot {
    fill: rgba(190, 231, 241, 0.9);
    filter: url(#workflow-soft-glow);
  }

  .workflow-icon {
    fill: none;
    stroke: rgba(255, 255, 255, 0.94);
    stroke-width: 5.4;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .workflow-icon-fill {
    fill: rgba(255, 255, 255, 0.94);
    stroke: none;
  }

  .workflow-envelope-fill {
    fill: rgba(255, 255, 255, 0.20);
    stroke: none;
  }

  .workflow-icon-asset,
  .workflow-ai-badge-asset {
    filter: drop-shadow(0 0 12px rgba(190, 231, 241, 0.22));
  }

  .workflow-cyan-dot {
    fill: #bee7f1;
    stroke: none;
    filter: url(#workflow-soft-glow);
  }

  .workflow-cyan-stroke {
    stroke: rgba(190, 231, 241, 0.88);
  }

  @media (prefers-reduced-motion: reduce) {
    .workflow-card-glow,
    .workflow-card-active-edge {
      transition: none;
    }
  }
`;

const salenzaFlow = keyframes`
  0% { stroke-dashoffset: 1; opacity: 0; }
  10% { opacity: 1; }
  76% { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0; }
`;

const gemeenteFlow = keyframes`
  0% { stroke-dashoffset: 620; opacity: 0; }
  8% { opacity: 1; }
  72% { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0; }
`;

const salenzaCorePulse = keyframes`
  0%, 100% { opacity: 0.38; transform: scale(0.98); }
  50% { opacity: 0.7; transform: scale(1.04); }
`;

const integrationMarquee = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const GemeenteVisualWrap = styled.div`
  position: relative;
  aspect-ratio: 1000 / 720;
  overflow: hidden;
  border-radius: 30px;
  background:
    radial-gradient(circle at 50% 48%, rgba(35, 211, 255, 0.12), transparent 34%),
    radial-gradient(circle at 82% 18%, rgba(14, 165, 233, 0.16), transparent 26%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.02)),
    rgba(3, 5, 6, 0.92);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    opacity: 0.09;
    background:
      linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px),
      linear-gradient(180deg, rgba(255,255,255,0.08) 1px, transparent 1px);
    background-size: 70px 70px;
  }

  @media (max-width: 780px) {
    aspect-ratio: 4 / 5;
  }
`;

const GemeenteFlowSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  .gemeente-panel {
    fill: rgba(0, 0, 0, 0.34);
    stroke: rgba(35, 211, 255, 0.26);
    stroke-width: 1.5;
  }

  .gemeente-source-card,
  .gemeente-output-card,
  .gemeente-metric-card {
    fill: rgba(0, 7, 10, 0.62);
    stroke: rgba(35, 211, 255, 0.26);
    stroke-width: 1.35;
  }

  .gemeente-source-card {
    fill: rgba(255, 255, 255, 0.035);
  }

  .gemeente-engine-glow {
    fill: rgba(35, 211, 255, 0.1);
    filter: url(#gemeenteFlowGlow);
  }

  .gemeente-engine-circle {
    fill: rgba(0, 8, 10, 0.88);
    stroke: rgba(35, 211, 255, 0.78);
    stroke-width: 1.7;
  }

  .gemeente-route-base {
    fill: none;
    stroke: rgba(35, 211, 255, 0.2);
    stroke-width: 3.4;
  }

  .gemeente-route-live {
    fill: none;
    stroke: url(#gemeenteFlowStroke);
    stroke-width: 5.4;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 150 620;
    stroke-dashoffset: 620;
    filter: url(#gemeenteFlowGlow);
    animation: ${gemeenteFlow} 5.2s linear infinite;
  }

  .gemeente-kicker,
  .gemeente-engine-kicker {
    fill: rgba(35, 211, 255, 0.96);
    font-size: 16px;
    font-weight: 900;
    letter-spacing: 5px;
  }

  .gemeente-source-text,
  .gemeente-output-title,
  .gemeente-engine-title,
  .gemeente-metric-value {
    fill: #fff;
    font-weight: 900;
  }

  .gemeente-source-text {
    font-size: 21px;
  }

  .gemeente-output-title {
    font-size: 23px;
  }

  .gemeente-output-body,
  .gemeente-engine-body {
    fill: rgba(255, 255, 255, 0.72);
    font-weight: 700;
  }

  .gemeente-output-body {
    font-size: 17px;
  }

  .gemeente-engine-kicker,
  .gemeente-engine-title,
  .gemeente-engine-body,
  .gemeente-metric-value,
  .gemeente-metric-label {
    text-anchor: middle;
  }

  .gemeente-engine-title {
    font-size: 34px;
  }

  .gemeente-engine-body {
    font-size: 19px;
  }

  .gemeente-metric-value {
    font-size: 28px;
  }

  .gemeente-metric-label {
    fill: rgba(255, 255, 255, 0.64);
    font-size: 13px;
    font-weight: 900;
    letter-spacing: 3px;
    text-transform: uppercase;
  }
`;

const SalenzaVisualWrap = styled.div`
  position: relative;
  aspect-ratio: 1000 / 720;
  overflow: hidden;
  border-radius: 30px;
  border: 1px solid rgba(39, 224, 147, 0.24);
  background:
    radial-gradient(circle at 50% 42%, rgba(39, 224, 147, 0.16), transparent 34%),
    radial-gradient(circle at 84% 18%, rgba(16, 185, 129, 0.16), transparent 28%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.018)),
    rgba(2, 10, 7, 0.94);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.07),
    inset 0 0 48px rgba(39, 224, 147, 0.055),
    0 30px 80px rgba(0, 0, 0, 0.38);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    opacity: 0.075;
    background:
      linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px),
      linear-gradient(180deg, rgba(255,255,255,0.08) 1px, transparent 1px);
    background-size: 74px 74px;
  }

  @media (max-width: 780px) {
    aspect-ratio: 1000 / 720;
    border-radius: 22px;
  }
`;

const SalenzaFlowSvg = styled.svg`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;

  .salenza-shell {
    fill: rgba(1, 8, 6, 0.56);
    stroke: rgba(255, 255, 255, 0.1);
    stroke-width: 1.3;
  }

  .salenza-stage {
    fill: rgba(0, 0, 0, 0.18);
    stroke: rgba(39, 224, 147, 0.14);
    stroke-width: 1.1;
  }

  .salenza-card rect {
    fill: rgba(0, 8, 6, 0.66);
    stroke: rgba(39, 224, 147, 0.26);
    stroke-width: 1.35;
  }

  .salenza-channel-card rect {
    fill: rgba(255, 255, 255, 0.04);
  }

  .salenza-platform-card rect {
    fill: rgba(0, 14, 10, 0.72);
    stroke: rgba(39, 224, 147, 0.34);
  }

  .salenza-action-card rect {
    fill: rgba(39, 224, 147, 0.06);
    stroke: rgba(39, 224, 147, 0.34);
  }

  .salenza-route-base {
    fill: none;
    stroke: rgba(39, 224, 147, 0.26);
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .salenza-route-live {
    fill: none;
    stroke: url(#salenzaFlowStroke);
    stroke-width: 4.8;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 0.2 0.8;
    stroke-dashoffset: 1;
    filter: url(#salenzaFlowGlow);
    animation: ${salenzaFlow} 4.8s linear infinite;
  }

  .salenza-core-halo {
    fill: rgba(39, 224, 147, 0.12);
    filter: url(#salenzaFlowGlow);
    transform-box: fill-box;
    transform-origin: center;
    animation: ${salenzaCorePulse} 4.6s ease-in-out infinite;
  }

  .salenza-core-ring {
    fill: rgba(0, 18, 12, 0.92);
    stroke: rgba(39, 224, 147, 0.76);
    stroke-width: 1.8;
  }

  .salenza-core-orbit {
    fill: none;
    stroke: rgba(80, 255, 183, 0.16);
    stroke-width: 1.4;
    stroke-linecap: round;
    stroke-dasharray: 7 16;
  }

  .salenza-core-orbit-bottom {
    opacity: 0.7;
  }

  .salenza-kicker,
  .salenza-core-subtitle {
    fill: rgba(80, 255, 183, 0.96);
    font-size: 20px;
    font-weight: 900;
    letter-spacing: 6.4px;
  }

  .salenza-kicker {
    text-anchor: middle;
  }

  .salenza-kicker-platforms {
    font-size: 13px;
    letter-spacing: 4.2px;
  }

  .salenza-card-title,
  .salenza-core-title {
    fill: #fff;
    font-weight: 900;
  }

  .salenza-card-title {
    font-size: 23px;
  }

  .salenza-platform-title {
    font-size: 20.5px;
  }

  .salenza-card-body {
    fill: rgba(255, 255, 255, 0.72);
    font-weight: 700;
  }

  .salenza-card-body {
    font-size: 15px;
  }

  .salenza-core-title,
  .salenza-core-subtitle {
    text-anchor: middle;
  }

  .salenza-core-title {
    font-size: 37px;
    letter-spacing: 0;
  }

  .salenza-core-subtitle {
    font-size: 10px;
    letter-spacing: 3px;
  }

  .salenza-platform-body {
    font-size: 14.2px;
  }

  .salenza-action-title,
  .salenza-action-body {
    text-anchor: middle;
  }

  .salenza-action-title {
    fill: rgba(255, 255, 255, 0.9);
    font-size: 18.5px;
    font-weight: 900;
    letter-spacing: 0.2px;
  }

  .salenza-action-body {
    fill: rgba(255, 255, 255, 0.62);
    font-size: 12.8px;
    font-weight: 800;
    letter-spacing: 0.05px;
  }

  .salenza-channel-logo {
    filter: drop-shadow(0 0 10px rgba(80, 255, 183, 0.2));
  }

  .salenza-channel-icon {
    fill: none;
    stroke: rgba(255, 255, 255, 0.94);
    stroke-width: 4.4;
    stroke-linecap: round;
    stroke-linejoin: round;
    filter: drop-shadow(0 0 8px rgba(80, 255, 183, 0.22));
  }

  .salenza-channel-icon-whatsapp {
    stroke-width: 3.9;
  }

  .salenza-channel-icon-instagram rect,
  .salenza-channel-icon-instagram circle {
    stroke: rgba(255, 255, 255, 0.94);
  }

  .salenza-channel-icon-email rect,
  .salenza-channel-icon-email path {
    stroke: rgba(255, 255, 255, 0.94);
  }

  @media (prefers-reduced-motion: reduce) {
    .salenza-route-live,
    .salenza-core-halo {
      animation: none;
    }
  }
`;

const CaseNarrativeBand = styled.section`
  max-width: 980px;
  display: grid;
  gap: 18px;
  padding: clamp(28px, 4vw, 48px) 0 clamp(10px, 2vw, 20px);
`;

const CaseNarrativeEyebrow = styled.div`
  color: var(--case-accent);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

const CaseNarrativeTitle = styled.h2`
  margin: 0;
  max-width: 900px;
  color: #fff;
  font-size: clamp(34px, 4.8vw, 72px);
  line-height: 0.98;
  letter-spacing: 0;
`;

const CaseNarrativeText = styled.p`
  margin: 0;
  max-width: 860px;
  color: rgba(255, 255, 255, 0.78);
  font-size: clamp(19px, 1.6vw, 26px);
  line-height: 1.55;
`;

const CaseStorySection = styled.section`
  display: grid;
  gap: clamp(22px, 3vw, 34px);
  padding: clamp(28px, 4vw, 44px);
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background:
    radial-gradient(circle at 12% 0%, rgba(190, 231, 241, 0.09), transparent 30%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.025)),
    rgba(7, 7, 8, 0.62);
`;

const CaseSectionHeader = styled.div`
  display: grid;
  gap: 12px;
  max-width: 820px;
`;

const CaseSectionEyebrow = styled.div`
  color: var(--case-accent);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

const CaseSectionTitle = styled.h3`
  margin: 0;
  color: #fff;
  font-size: clamp(30px, 3.2vw, 50px);
  line-height: 1.02;
  letter-spacing: 0;
`;

const CaseSectionLead = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.76);
  font-size: clamp(18px, 1.25vw, 21px);
  line-height: 1.66;
`;

const CaseStoryItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const CaseStoryItem = styled.article`
  min-height: 210px;
  display: grid;
  align-content: start;
  gap: 12px;
  padding: clamp(20px, 2vw, 26px);
  border-radius: 22px;
  border: 1px solid var(--case-accent-border);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018)),
    rgba(0, 0, 0, 0.42);
`;

const CaseStoryItemNumber = styled.span`
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.96);
  border: 1px solid var(--case-accent-border-strong);
  background: rgba(0, 0, 0, 0.56);
  font-size: 14px;
  font-weight: 900;
`;

const CaseStoryItemTitle = styled.h4`
  margin: 0;
  color: #fff;
  font-size: clamp(22px, 1.7vw, 28px);
  line-height: 1.05;
`;

const CaseStoryItemText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 17px;
  line-height: 1.58;
`;

const CaseIntegrationsPanel = styled.section`
  display: grid;
  gap: clamp(22px, 3vw, 32px);
  padding: clamp(28px, 4vw, 44px) 0;
  border-top: 1px solid rgba(var(--case-accent-rgb), 0.18);
  border-bottom: 1px solid rgba(var(--case-accent-rgb), 0.14);
`;

const CaseIntegrationViewport = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding: 6px 0;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 9%, #000 91%, transparent);
  mask-image: linear-gradient(90deg, transparent, #000 9%, #000 91%, transparent);
`;

const CaseIntegrationTrack = styled.div`
  display: flex;
  align-items: stretch;
  gap: clamp(14px, 1.5vw, 22px);
  width: max-content;
  animation: ${integrationMarquee} 36s linear infinite;
  will-change: transform;

  &:hover {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    flex-wrap: wrap;
    width: 100%;
  }
`;

const CaseIntegrationBanner = styled.article`
  width: clamp(235px, 18vw, 294px);
  min-height: 92px;
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  align-items: center;
  gap: 15px;
  padding: 18px 20px;
  border-radius: 18px;
  border: 1px solid rgba(var(--case-accent-rgb), 0.22);
  background:
    radial-gradient(circle at 14% 26%, rgba(var(--case-accent-rgb), 0.12), transparent 38%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.065), rgba(255, 255, 255, 0.018)),
    rgba(0, 0, 0, 0.46);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 16px 42px rgba(0, 0, 0, 0.26);
`;

const CaseIntegrationLogoSlot = styled.div`
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  border-radius: 16px;
  border: 1px solid rgba(var(--case-accent-rgb), 0.18);
  background: ${(p) =>
    p.$logo
      ? "rgba(255, 255, 255, 0.94)"
      : "linear-gradient(145deg, rgba(var(--case-accent-rgb), 0.18), rgba(255, 255, 255, 0.045)), rgba(0, 0, 0, 0.38)"};
  overflow: hidden;

  svg {
    width: 31px;
    height: 31px;
    fill: rgba(255, 255, 255, 0.9);
  }

  .integration-email-icon {
    fill: none;
    stroke: rgba(255, 255, 255, 0.9);
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  span {
    color: rgba(255, 255, 255, 0.9);
    font-size: 17px;
    font-weight: 900;
    letter-spacing: 0.05em;
  }
`;

const CaseIntegrationLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const CaseIntegrationText = styled.div`
  min-width: 0;
  display: grid;
  gap: 5px;

  strong {
    color: rgba(255, 255, 255, 0.96);
    font-size: clamp(20px, 1.35vw, 24px);
    line-height: 1.02;
    letter-spacing: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    color: rgba(255, 255, 255, 0.62);
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 0.11em;
    text-transform: uppercase;
  }
`;

const CaseIntegrationPending = styled.span`
  grid-column: 1 / -1;
  width: fit-content;
  margin-top: -3px;
  padding: 5px 9px;
  border-radius: 999px;
  border: 1px solid rgba(var(--case-accent-rgb), 0.28);
  color: rgba(255, 255, 255, 0.68);
  background: rgba(var(--case-accent-rgb), 0.08);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const CaseWebAppPanel = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(280px, 0.65fr);
  gap: clamp(22px, 3vw, 36px);
  align-items: start;
  padding: clamp(28px, 4vw, 44px);
  border-radius: 30px;
  border: 1px solid rgba(190, 231, 241, 0.18);
  background:
    radial-gradient(circle at 90% 8%, rgba(190, 231, 241, 0.12), transparent 30%),
    linear-gradient(145deg, rgba(190, 231, 241, 0.06), rgba(255, 255, 255, 0.018)),
    rgba(5, 7, 8, 0.7);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CaseWebAppCopy = styled.div`
  display: grid;
  gap: 12px;
`;

const CaseWebAppList = styled.ul`
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;
  list-style: none;
`;

const CaseWebAppItem = styled.li`
  position: relative;
  padding: 13px 14px 13px 34px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.82);
  background: rgba(0, 0, 0, 0.26);
  line-height: 1.45;

  &::before {
    content: "";
    position: absolute;
    left: 15px;
    top: 1.55em;
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: rgba(190, 231, 241, 0.9);
    box-shadow: 0 0 12px rgba(190, 231, 241, 0.38);
  }
`;

const CaseStoryPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 0.88fr) minmax(0, 0.72fr);
  gap: clamp(20px, 3vw, 34px);
  align-items: center;
  padding: clamp(16px, 2vw, 24px);
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.02)),
    rgba(7, 7, 8, 0.58);

  .story-quote-title {
    margin: 0;
    max-width: 40ch;
    font-size: clamp(20px, 1.75vw, 28px);
    line-height: 1.48;
    font-weight: 650;
    letter-spacing: 0;
    text-transform: none;
    opacity: 0.9;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;

    .story-quote-title {
      max-width: none;
      font-size: clamp(20px, 5.2vw, 27px);
      line-height: 1.44;
    }
  }
`;

const CaseStoryMedia = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #050505;
`;

const CaseStoryCopy = styled.div`
  display: grid;
  gap: 12px;
`;

const CaseEngineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const CaseEngineCard = styled.div`
  min-height: 260px;
  display: grid;
  align-content: start;
  gap: 16px;
  padding: clamp(22px, 2.7vw, 32px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.022)),
    rgba(7, 7, 8, 0.58);
`;

const CaseEngineTitle = styled.h4`
  margin: 0 0 2px;
  color: var(--case-accent);
  font-size: clamp(13px, 0.9vw, 15px);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.9;
`;

const CaseEngineText = styled.p`
  margin: 0;
  font-size: clamp(17px, 1.1vw, 19px);
  line-height: 1.68;
  color: rgba(255, 255, 255, 0.84);
`;

const CaseOutcomeList = styled.ul`
  margin: 2px 0 0;
  padding-left: 18px;
  display: grid;
  gap: 8px;
  color: rgba(255, 255, 255, 0.78);

  li {
    line-height: 1.55;
  }
`;

const CaseQuote = styled.blockquote`
  margin: 0;
  display: grid;
  gap: 12px;
  max-width: 920px;
  padding: clamp(22px, 3vw, 34px);
  border-left: 2px solid rgba(177, 239, 248, 0.6);
  background: rgba(255, 255, 255, 0.035);

  p {
    margin: 0;
    font-size: clamp(24px, 3vw, 38px);
    line-height: 1.18;
    font-weight: 800;
  }

  span {
    color: rgba(255, 255, 255, 0.62);
  }
`;

const CaseStackPanel = styled.div`
  display: grid;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

const CaseNexPanel = styled.section`
  position: relative;
  overflow: hidden;
  display: grid;
  gap: clamp(24px, 3vw, 36px);
  padding: clamp(28px, 4vw, 46px);
  border-radius: 30px;
  border: 1px solid var(--case-accent-border);
  background:
    radial-gradient(circle at 12% 14%, var(--case-accent-bg), transparent 30%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.018)),
    rgba(5, 7, 8, 0.72);

  &::before {
    content: "";
    position: absolute;
    inset: 18px;
    border-radius: 24px;
    border: 1px solid rgba(255, 255, 255, 0.055);
    pointer-events: none;
  }
`;

const CaseNexHeader = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(190px, 0.32fr) minmax(0, 1fr);
  gap: clamp(22px, 4vw, 52px);
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const CaseNexLogoWrap = styled.div`
  min-height: 180px;
  display: grid;
  place-items: center;
  padding: 28px;
  border-radius: 24px;
  border: 1px solid var(--case-accent-border);
  background:
    radial-gradient(circle at 50% 45%, rgba(190, 231, 241, 0.12), transparent 46%),
    rgba(0, 0, 0, 0.32);
  color: rgba(255, 255, 255, 0.92);
  font-size: clamp(38px, 6vw, 72px);
  font-weight: 900;
  letter-spacing: 0.08em;
`;

const CaseNexLogo = styled.img`
  width: min(240px, 70%);
  max-height: 88px;
  object-fit: contain;
  filter: drop-shadow(0 0 22px rgba(190, 231, 241, 0.18));
`;

const CaseNexCopy = styled.div`
  display: grid;
  gap: 12px;
`;

const CaseNexLearnLink = styled.a`
  width: max-content;
  margin-top: 8px;
  color: var(--case-accent);
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-decoration: none;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: translateX(2px);
    opacity: 0.9;
  }
`;

const CaseNexProcessGrid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const CaseNexProcessItem = styled.article`
  min-height: 168px;
  display: grid;
  align-content: start;
  gap: 14px;
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.28);
`;

const CaseNexProcessNumber = styled.span`
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  border: 1px solid var(--case-accent-border-strong);
  color: var(--case-accent);
  font-size: 12px;
  font-weight: 900;
`;

const CaseNexProcessText = styled.p`
  margin: 0;
  display: grid;
  gap: 8px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 16px;
  line-height: 1.52;

  strong {
    color: #fff;
    font-size: 18px;
    line-height: 1.12;
  }
`;

const CaseContactPanel = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: clamp(20px, 3vw, 36px);
  align-items: center;
  padding: clamp(26px, 4vw, 42px);
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.025)),
    rgba(7, 7, 8, 0.72);

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const CaseContactCopy = styled.div`
  display: grid;
  gap: 12px;
  max-width: 820px;
`;

const CaseContactTitle = styled.h3`
  margin: 0;
  color: #fff;
  font-size: clamp(34px, 4vw, 58px);
  line-height: 1;
  letter-spacing: 0;
`;

const CaseContactActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 860px) {
    justify-content: flex-start;
  }
`;

const Header = styled.div`
  width: min(calc(100% - (var(--content-gutter) * 2)), var(--content-wide-width));
  margin: 0 auto;
  text-align: left;
`;

const Kicker = styled.div`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 14px;
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  margin: 18px 0 12px;
  font-size: clamp(38px, 5vw, 62px);
  line-height: 0.98;
`;

const Lead = styled.p`
  font-family: var(--font-body);
  margin: 0;
  opacity: 0.82;
  max-width: min(46rem, 100%);
  line-height: 1.72;
`;

const Ambient = styled.div`
  position: absolute; inset: 0; pointer-events: none; z-index: 0;
`;

const wipBgMove = keyframes`
  0%   { background-position: 0px 0px, 0px 0px, 0px 0px; }
  100% { background-position: 200px 200px, 0px 0px, 0px 0px; }
`;

const wipScan = keyframes`
  0%   { opacity: 0.15; transform: translateY(-100%); }
  100% { opacity: 0; transform: translateY(100%); }
`;


const Grid = styled.div`
  width: min(calc(100% - (var(--content-gutter) * 2)), var(--content-wide-width));
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 20px;
  z-index: 1;

  @media (max-width: 960px){ grid-template-columns: repeat(6, 1fr); }
  @media (max-width: 560px){ grid-template-columns: repeat(1, 1fr); }
`;

const CardWrap = styled(motion.button)`
  grid-column: span 6;

  position: relative; display: flex; flex-direction: column; align-items: stretch;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 20px; overflow: hidden; cursor: pointer;
  transform-style: preserve-3d;
  padding: 0; text-align: left; color: inherit;

  &:focus-visible{ outline: 2px solid rgba(255,255,255,.5); outline-offset: 2px; }
  &[data-locked]{ cursor: default; opacity: .8; }
  &[data-hidden]{ visibility: hidden; }
`;

const Cover = styled(motion.div)`
  position: relative;
  height: clamp(180px, 24vw, 320px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-bottom: 1px solid rgba(255,255,255,0.12);

  /* base bg */
  background: ${({ $hasImg, $locked }) =>
    $hasImg
      ? `linear-gradient(135deg, #0f0f0f, #1a1a1a)`
      : $locked
      ? `
        radial-gradient(circle at 20% 20%, rgba(255,255,255,0.07) 0%, rgba(0,0,0,0) 60%),
        repeating-linear-gradient(
          135deg,
          rgba(255,255,255,0.06) 0px,
          rgba(255,255,255,0.06) 2px,
          rgba(0,0,0,0) 2px,
          rgba(0,0,0,0) 6px
        ),
        radial-gradient(120% 100% at 20% 0%, #1d1d1d 0%, #0b0b0b 70%)
      `
      : `radial-gradient(120% 100% at 20% 0%, #1d1d1d, #0b0b0b)`};

  background-size: ${({ $locked }) =>
    $locked
      ? `200px 200px, 8px 8px, cover`
      : `cover`};

  /* dark vignette */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(
      circle at 50% 20%,
      rgba(0,0,0,0) 0%,
      rgba(0,0,0,0.6) 70%
    );
  }

  ${({ $locked }) =>
    $locked &&
    css`
      animation: ${wipBgMove} 6s linear infinite;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        height: 40%;
        top: 0;
        pointer-events: none;
        background: linear-gradient(
          to bottom,
          rgba(255,255,255,0.35) 0%,
          rgba(0,0,0,0) 80%
        );
        mix-blend-mode: screen;
        animation: ${wipScan} 2.5s linear infinite;
      }
    `}
`;



const Shine = styled(motion.div)`
  position: absolute; inset: 0; pointer-events: none; mix-blend-mode: screen;
`;

const CardBorder = styled.div`
  position: absolute; inset: 0; border-radius: 20px; pointer-events: none;
  border: 1px solid rgba(255,255,255,0.1);
  &::after{
    content:""; position:absolute; inset:-1px; border-radius:inherit; padding:1px;
    background: linear-gradient(135deg, rgba(255,255,255,.35), rgba(255,255,255,.08));
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor; mask-composite: exclude; opacity:.35;
  }
`;

const CardBody = styled.div`
  padding: 16px 16px 18px;
  display: grid;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`;

const CardTitle = styled.h3`
  margin: 0; font-size: 20px; font-weight: 800;
`;

const Year = styled.span`
  opacity: .7; font-size: 14px;
`;

const Sub = styled.p`
  margin: 0;
  opacity: 0.9;
  max-width: 44ch;
`;

const Badges = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const Badge = styled.span`
  font-size: 11px; letter-spacing: .06em; font-weight: 800; text-transform: uppercase;
  border: 1px solid rgba(255,255,255,.22); border-radius: 999px; padding: 4px 8px; opacity: .9;
`;

/* ===== Modal / Detail ===== */

const Backdrop = styled(motion.div)`
  position: fixed; inset: 0; z-index: ${Z};
  background: rgba(0,0,0,.45); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  border: 0; margin: 0; padding: 0; cursor: default;
`;

const Sheet = styled(motion.div)`
  position: fixed; inset: 0; z-index: ${Z + 1}; pointer-events: auto;
`;

const Close = styled(motion.button)`
  position: absolute;
  top: 20px;
  left: 20px;
  right: auto;
  z-index: 999; /* higher than header/logo just to be safe */
  pointer-events: auto;

  height: 40px;
  width: 40px;
  border-radius: 999px;
  display: grid;
  place-items: center;

  border: 1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.06);

  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
  }

  @media (max-width: 560px) {
    top: 16px;
    left: 16px;
    height: 44px;
    width: 44px;
  }
`;


const Detail = styled(motion.div)`
  pointer-events: auto;
  position: absolute;
  inset: 0;
  margin: auto;
  width: min(1100px, 92vw);
  border-radius: 22px;
  background: linear-gradient(160deg, rgba(12,12,16,0.96), rgba(6,6,10,0.94));
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);

  /* modal sizing in viewport */
  top: clamp(40px, 6vh, 80px);
  bottom: clamp(40px, 6vh, 80px);

  /* important change: behave like a page */
  display: flex;
  flex-direction: column;

  /* scroll happens here now */
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: none;

  box-shadow: 0 6px 28px rgba(0,0,0,.5);
`;


const CoverImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${({ $fit }) => $fit || "contain"};
  object-position: ${({ $position }) => $position || "center"};
  transform: scale(${({ $scale }) => $scale || 1});
  /* Prevent blurring on downscale in mobile Safari */
  image-rendering: -webkit-optimize-contrast;
`;


const lockPulse = keyframes`
  0%   { box-shadow: 0 0 8px rgba(255,255,255,0.4); }
  70%  { box-shadow: 0 0 16px rgba(255,255,255,0); }
  100% { box-shadow: 0 0 8px rgba(255,255,255,0.4); }
`;

const LockedOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 16px;
  z-index: 2;
`;

const LockedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.4);
  background: rgba(0,0,0,.6);
  color: #fff;
  animation: ${lockPulse} 2.5s ease-in-out infinite;
`;

const LockedText = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255,255,255,0.8);
`;



const DetailCover = styled(motion.div)`
  flex-shrink: 0;
  height: clamp(220px, 36vh, 360px);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  background: ${({ $hasImg }) =>
    $hasImg
      ? `linear-gradient(135deg, #0f0f0f, #1a1a1a)`
      : `linear-gradient(135deg, #151515, #0b0b0b)`};
`;


const DetailBody = styled.div`
  padding: clamp(16px, 2vw, 24px);
  display: grid;
  gap: clamp(12px, 2vw, 18px);
  background: rgba(12,12,18,0.78);
  border-radius: 0 0 22px 22px;

  /* no internal scrolling, it just flows */
  min-height: auto;
  height: auto;
`;

const DetailTitle = styled.h2`
  margin: 0; font-size: clamp(22px, 4vw, 32px); font-weight: 900;
`;

const DetailSub = styled.p`
  margin: 0; opacity: .9; max-width: 78ch;
`;

const Columns = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 14px;
  @media (max-width: 720px){ grid-template-columns: 1fr; }
`;

const InfoCard = styled(motion.div)`
  border: 1px solid rgba(255,255,255,.12); border-radius: 16px; padding: 14px;
  background: rgba(255,255,255,.04);
  box-shadow: 0 12px 32px rgba(0,0,0,0.35);
`;

const InfoTitle = styled.h4`
  margin: 0 0 8px; font-size: 12px; letter-spacing: .12em; text-transform: uppercase; opacity: .7;
`;

const List = styled.ul`
  margin: 0 auto;
  padding-left: 18px;
  display: grid;
  gap: 6px;
  max-width: 60ch;
  text-align: left;
  li {
    line-height: 1.6;
  }
`;

const Tags = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px;
`;

const Tag = styled.span`
  font-size: 12px; border: 1px solid rgba(255,255,255,.18); border-radius: 999px; padding: 6px 10px; opacity: .9;
`;

const CTARow = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap; padding-top: 12px; border-top: 1px solid rgba(255,255,255,.08);
`;

const CTA = styled.a`
  display: inline-flex; align-items: center; gap: 8px; text-decoration: none; font-weight: 800;
  padding: 10px 14px; border-radius: 12px; background: #fff; color: #000;
  &:hover{ transform: translateY(-1px); }
`;

const CTASecondary = styled.a`
  display: inline-flex; align-items: center; gap: 8px; text-decoration: none; font-weight: 800;
  padding: 10px 14px; border-radius: 12px; background: rgba(255,255,255,.06); color: #fff; border: 1px solid rgba(255,255,255,.18);
  &:hover{ transform: translateY(-1px); }
`;


const SectionBlock = styled.div`
  display: grid;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid rgba(255,255,255,0.08);
  text-align: center;
`;

const SectionHeading = styled.h3`
  margin: 0;
  font-size: 14px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.7;
`;

const SectionText = styled.p`
  margin: 0 auto;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.9;
  max-width: 70ch;
`;

const ScreenshotBlock = styled.div`
  margin: 12px auto 0;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255,255,255,0.02);
  box-shadow: 0 6px 24px rgba(0,0,0,0.5);
  max-width: 680px;
`;

const ScreenshotImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  background: #0b0b0b;
`;

const ScreenshotCaption = styled.div`
  font-size: 12px;
  line-height: 1.5;
  opacity: 0.7;
  padding: 8px 12px;
  border-top: 1px solid rgba(255,255,255,0.08);
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(2px);
`;

/* ========================= Helpers ========================= */

// lightweight helper so we can use motion values inside template literals
function useMotion(mv, map) {
  const [v, setV] = useState(map(mv.get()));
  useEffect(() => mv.on("change", (n) => setV(map(n))), [mv, map]);
  return v;
}
